import * as React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';

import { Text, TYPOGRAPHY } from 'wix-ui-tpa/Text';
import { Box, Spinner } from '@wix/social-groups-common';
import { compose } from '@wix/social-groups-common/dist/src/compose';
import { isJoined, memberWrapper } from '@wix/social-groups-api';
import {
  ApiTypes,
  ReactionsApiTypes,
} from '@wix/social-groups-api/dist/src/types';

import { IFeedItem } from '../../../../controllers/types';
import { PostContentViewer } from '../../PostContentEditor/PostContentViewer'; //long imports == better performance
import { SiteMembers } from '../../Context/SiteMembers';
import {
  withAppSettings,
  WithAppSettingsProps,
} from '../../Context/withAppSettings';

const Reactions = React.lazy(() => import('../Reactions/Reactions'));

const ReactedMembers = React.lazy(() =>
  import('../Reactions/ReactedMembers/ReactedMembers'),
);
const Comments = React.lazy(() => import('../Comments/Comments'));
import { AuthorInfo } from '../AuthorInfo';
import { PostActions } from '../PostActions';

import styles from './FeedItem.st.css';
import { ShareButton } from '../../Share';
import {
  withTpaComponentsConfig,
  WithTpaComponentsConfigProps,
} from '../../Context/withTpaComponentsConfig';
import {
  MembershipChangeAction,
  WithGroup,
  WithGroupProps,
  withMembershipChangeAction,
  withSiteMembers,
} from '../../Context';
import { WithSiteMembers } from '../../Context/withSiteMembers';

export interface FeedItemProps {
  feedItem: IFeedItem;
  siteMembersMap: SiteMembers['siteMembersMap'];
  postAuthor: ApiTypes.v1.GroupMemberResponse;
  currentMember: ApiTypes.v1.GroupMemberResponse;
  contextToken: string;

  onDeletePostClick(feedItemId: string);

  onPinPostClick(feedItemId: string): void;

  onUnpinPostClick(feedItemId: string): void;

  onFollowPostClick(feedItemId: string): void;

  onUnfollowPostClick(feedItemId: string): void;

  onUpdatePostClick(feedItemId: string, entity): void;

  react(feedItemId: string, reaction: ReactionsApiTypes.Reaction): void;

  unreact(feedItemId: string, reactionCode: string): void;

  onShare();
}

interface FeedItemComponentState {
  totalComments: number;
  showComments: boolean;
  didMount: boolean;
}

export class FeedItemComponent extends React.Component<
  FeedItemProps &
    InjectedTranslateProps &
    WithTpaComponentsConfigProps &
    WithAppSettingsProps &
    WithSiteMembers &
    WithGroupProps &
    MembershipChangeAction,
  FeedItemComponentState
> {
  state: FeedItemComponentState = {
    didMount: false,
    totalComments: 0,
    showComments: true,
  };

  componentDidMount(): void {
    this.setState({ didMount: true });
  }

  handleReact = (reaction: ReactionsApiTypes.Reaction) => {
    this.handleProtectedActionClick(() => {
      this.props.react(this.props.feedItem.feedItemId, reaction);
    });
  };

  handleUnreact = (reactionCode: string) => {
    this.handleProtectedActionClick(() => {
      this.props.unreact(this.props.feedItem.feedItemId, reactionCode);
    });
  };

  handleProtectedActionClick = (protectedAction: Function) => {
    // !!! Can not use currentMember here, because it is not updated after changing membership
    const { group, isLoggedIn, promptLogin, openJoinDialog } = this.props;
    if (!isLoggedIn) {
      promptLogin();
      return;
    }

    if (!isJoined(group)) {
      openJoinDialog();
      return;
    }

    protectedAction();
  };

  toggleComments = () =>
    this.setState({ showComments: !this.state.showComments });

  render() {
    const {
      appSettings,
      feedItem,
      t,
      postAuthor,
      siteMembersMap,
      onDeletePostClick,
      onUpdatePostClick,
      onPinPostClick,
      onUnpinPostClick,
      onFollowPostClick,
      onUnfollowPostClick,
      onShare,
      mobile,
      contextToken,
    } = this.props;

    const { showComments, didMount } = this.state;

    const contentState = JSON.parse(feedItem.entity.body.content);
    const { feedItemId } = feedItem;
    const { name, imageUrl, relationship, roles } = memberWrapper(postAuthor);

    const { showShareButton } = appSettings.discussionsDisplay;

    return (
      <Box
        {...styles('root', {
          mobile,
          withReactions: feedItem.reactions.total > 0,
        })}
      >
        <div className={styles.meta}>
          <AuthorInfo
            name={name.nick || t('groups-web.member.anonymous')}
            avatarUrl={imageUrl}
            timeStamp={feedItem.createdAt}
            relationship={relationship}
            roles={roles}
          />
          {showShareButton && <ShareButton onClick={onShare} />}
          <PostActions
            itemId={feedItemId}
            isPinnedPost={!!feedItem.pin}
            isFollowedPost={
              feedItem.requesterContext && feedItem.requesterContext.subscribed
            }
            postAuthor={postAuthor}
            deletePost={() => onDeletePostClick(feedItemId)}
            updatePost={entity => onUpdatePostClick(feedItemId, entity)}
            pinPost={() => {
              onPinPostClick(feedItemId);
            }}
            unpinPost={() => {
              onUnpinPostClick(feedItemId);
            }}
            followPost={() => {
              this.handleProtectedActionClick(() => {
                onFollowPostClick(feedItemId);
              });
            }}
            unfollowPost={() => {
              this.handleProtectedActionClick(() => {
                onUnfollowPostClick(feedItemId);
              });
            }}
            contentState={contentState}
            onShare={onShare}
          />
        </div>
        <div className={styles.viewer}>
          <PostContentViewer contentState={contentState} />
        </div>
        {this.renderReactions()}
        {showComments && didMount /* TODO: Comments are not ready for SSR */ ? (
          <React.Suspense fallback={<Spinner />}>
            <Comments
              memberAuth={this.handleProtectedActionClick}
              token={contextToken}
              entityID={feedItem.feedItemId}
              updateTotal={this.updateTotalComments}
            />
          </React.Suspense>
        ) : null}
      </Box>
    );
  }

  private renderReactions() {
    const { didMount } = this.state;
    if (!didMount) {
      return null;
    }
    const { feedItem, t, siteMembersMap, appSettings } = this.props;

    const { showReactions } = appSettings.discussionsDisplay;

    const hasReactions = !!feedItem.reactions.usersReacted.total;
    return (
      <React.Suspense fallback={<Spinner />}>
        <div className={styles.reactionsAndMeta}>
          {showReactions && (
            <div className={styles.reactions}>
              <Reactions
                siteMembersMap={siteMembersMap}
                reactions={feedItem.reactions}
                react={this.handleReact}
                unreact={this.handleUnreact}
              />

              {!hasReactions && (
                <span onClick={this.toggleComments}>
                  <Text
                    {...styles('totalComments')}
                    typography={TYPOGRAPHY.listText}
                  >
                    {t('groups-web.discussion.feed.total-comments', {
                      count: this.state.totalComments,
                    })}
                  </Text>
                </span>
              )}
            </div>
          )}

          {(hasReactions || !showReactions) && (
            <div className={styles.meta}>
              <ReactedMembers
                siteMembersMap={siteMembersMap}
                reactions={feedItem.reactions}
              />
              <span onClick={this.toggleComments}>
                <Text
                  {...styles('totalComments')}
                  typography={TYPOGRAPHY.listText}
                >
                  {t('groups-web.discussion.feed.total-comments', {
                    count: this.state.totalComments,
                  })}
                </Text>
              </span>
            </div>
          )}
        </div>
      </React.Suspense>
    );
  }

  private readonly updateTotalComments = (totalComments: number) => {
    this.setState({ totalComments });
  };
}

const enhance = compose(
  translate(),
  withTpaComponentsConfig,
  withAppSettings,
  WithGroup,
  withSiteMembers,
  withMembershipChangeAction,
);

export const FeedItem = enhance(FeedItemComponent) as React.ComponentType<
  FeedItemProps
>;
